import { useEffect, useRef, useState } from "react";
import heroVideo from "../assets/video/video1.mp4";
import { Link } from "react-router-dom";

export default function VideoHeroBanner({
  title = "Fresh, Organic & Local",
  subtitle = "Delivered straight from Manchester to your doorstep.",
  ctaLabel = "Shop Now",
  ctaTo = "/products",
}) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const userPaused = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [soundOn, setSoundOn] = useState(false);

  // Browsers only allow a video to autoplay while muted. The visitor turns sound on with the
  // button, and that click is what lets the browser allow it. Setting the property directly is also
  // more reliable than the React "muted" attribute alone.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !soundOn;
  }, [soundOn]);

  // Play only while the banner is on screen (saves data and battery), and not at all
  // for visitors who have asked their device to reduce motion.
  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return undefined;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      userPaused.current = true;
      return undefined;
    }

    const tryPlay = () => {
      video.play().catch(() => {
        // the browser refused autoplay: the Play button is still there
      });
    };

    if (!("IntersectionObserver" in window)) {
      tryPlay();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!userPaused.current) tryPlay();
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  function togglePlayback() {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      userPaused.current = false;
      video.play().catch(() => {});
    } else {
      userPaused.current = true;
      video.pause();
    }
  }

  return (
    <div className="video-hero-container" ref={containerRef}>
      <video
        ref={videoRef}
        className="video-hero"
        src={heroVideo}
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <div className="video-hero-overlay">
        <h2>{title}</h2>
        <p>{subtitle}</p>

        <Link to={ctaTo} className="ui-btn ui-btn--primary video-hero-cta">
          {ctaLabel}
        </Link>
      </div>

      <div className="video-hero-controls">
        <button
          type="button"
          className="video-hero-toggle"
          onClick={() => setSoundOn((on) => !on)}
          aria-pressed={soundOn}
          aria-label={soundOn ? "Turn video sound off" : "Turn video sound on"}
        >
          {soundOn ? "Sound off" : "Sound on"}
        </button>

        <button
          type="button"
          className="video-hero-toggle"
          onClick={togglePlayback}
          aria-label={playing ? "Pause background video" : "Play background video"}
        >
          {playing ? "Pause" : "Play"}
        </button>
      </div>
    </div>
  );
}